import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Search,
  Eye,
  EyeOff,
  Clock,
  Euro,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  RefreshCw,
  CalendarX,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePermissions } from "../hooks/usePermissions";

interface Service {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  durationMinutes: number;
  bufferMinutes: number;
  slotInterval?: number | null;
  price: number;
  color: string;
  requiresDeposit: boolean;
  depositAmount: number | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  name: string;
  description: string;
  category: string;
  durationMinutes: string;
  slotInterval: string;
  price: string;
  color: string;
  requiresDeposit: boolean;
  depositAmount: string;
  isActive: boolean;
}

const EMPTY_FORM: FormData = {
  name: "",
  description: "",
  category: "",
  durationMinutes: "30",
  slotInterval: "",
  price: "0",
  color: "#10b981",
  requiresDeposit: false,
  depositAmount: "",
  isActive: true,
};

const COLOR_PRESETS = [
  "#10b981", // emerald
  "#6366f1", // indigo
  "#ec4899", // pink
  "#f59e0b", // amber
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ef4444", // red
  "#14b8a6", // teal
];

// Extrae un mensaje de error legible del cuerpo JSON (`error.response.data` de
// axios). El backend puede devolver `{ message }` (permisos, 404) o
// `{ message, details: [{ message }] }` en validaciones de Zod más
// específicas; en ese último caso `details[0].message` es más útil que el
// genérico. Si no hay nada reconocible, se usa `fallback`.
function extractApiErrorMessage(err: unknown, fallback: string): string {
  const errData = (err as { response?: { data?: unknown } })?.response?.data;
  if (errData && typeof errData === "object") {
    const obj = errData as {
      message?: string;
      details?: { message?: string }[];
    };
    const firstDetail = Array.isArray(obj.details)
      ? obj.details.find((d) => typeof d?.message === "string" && d.message)
      : undefined;
    if (firstDetail?.message) return firstDetail.message;
    if (typeof obj.message === "string" && obj.message) return obj.message;
  }
  return fallback;
}

// Valida un importe monetario introducido en texto libre (admite coma o punto
// decimal, hasta 2 decimales). Devuelve `null` si está vacío o no es un
// número válido ≥ 0, en vez de forzarlo silenciosamente a 0.
function parseMoneyAmount(value: string): number | null {
  const trimmed = value.trim().replace(",", ".");
  if (trimmed === "" || !/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const n = parseFloat(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export default function Services() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  const canManage = can("servicios:editar");
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<{
    message: string;
    isBookingModeMismatch: boolean;
  } | null>(null);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [reorderingId, setReorderingId] = useState<string | null>(null);

  useEffect(() => {
    loadServices();
  }, [showInactive]);

  const loadServices = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const res = await apiClient.get("/services", {
        params: { includeInactive: showInactive ? "true" : "false" },
      });
      setServices(res.data?.data ?? []);
    } catch (err) {
      const response = (
        err as { response?: { status?: number; data?: unknown } }
      )?.response;
      const status = response?.status;
      const data = response?.data as
        | { code?: string; message?: string }
        | undefined;
      const message = data?.message ?? "";
      const isBookingModeMismatch =
        status === 403 &&
        (data?.code === "BOOKING_MODE_MISMATCH" ||
          message.includes("BOOKING_MODE_MISMATCH"));
      setLoadError({
        message: isBookingModeMismatch
          ? "Esta sección es solo para negocios que trabajan con citas."
          : message || "No se pudieron cargar los servicios",
        isBookingModeMismatch,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return services;
    return services.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.category ?? "").toLowerCase().includes(q) ||
        (s.description ?? "").toLowerCase().includes(q),
    );
  }, [services, search]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (s: Service) => {
    setEditingId(s.id);
    setFormData({
      name: s.name,
      description: s.description ?? "",
      category: s.category ?? "",
      durationMinutes: String(s.durationMinutes),
      slotInterval:
        s.slotInterval !== null && s.slotInterval !== undefined
          ? String(s.slotInterval)
          : "",
      price: String(s.price),
      color: s.color,
      requiresDeposit: s.requiresDeposit,
      depositAmount:
        s.depositAmount !== null && s.depositAmount !== undefined
          ? String(s.depositAmount)
          : "",
      isActive: s.isActive,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast("El nombre es obligatorio", "error");
      return;
    }
    const dur = parseInt(formData.durationMinutes, 10);
    if (!Number.isFinite(dur) || dur < 5 || dur > 480) {
      showToast("La duración debe estar entre 5 y 480 minutos", "error");
      return;
    }
    const slotIntervalTrimmed = formData.slotInterval.trim();
    let slotInterval: number | null = null;
    if (slotIntervalTrimmed !== "") {
      const parsedInterval = parseInt(slotIntervalTrimmed, 10);
      if (
        !Number.isFinite(parsedInterval) ||
        parsedInterval < 5 ||
        parsedInterval > 180
      ) {
        showToast(
          "El intervalo entre slots debe quedar vacío o ser un número entre 5 y 180 minutos",
          "error",
        );
        return;
      }
      slotInterval = parsedInterval;
    }

    const price = parseMoneyAmount(formData.price);
    if (price === null) {
      showToast(
        "El precio debe ser un número válido igual o mayor que 0",
        "error",
      );
      return;
    }

    let deposit: number | null = null;
    if (formData.depositAmount.trim() !== "") {
      deposit = parseMoneyAmount(formData.depositAmount);
      if (deposit === null) {
        showToast(
          "El importe del anticipo debe ser un número válido igual o mayor que 0",
          "error",
        );
        return;
      }
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      category: formData.category.trim() || null,
      durationMinutes: dur,
      slotInterval,
      price,
      color: formData.color,
      requiresDeposit: formData.requiresDeposit,
      depositAmount: formData.requiresDeposit ? deposit : null,
      isActive: formData.isActive,
    };

    try {
      setIsSubmitting(true);
      if (editingId) {
        await apiClient.put(`/services/${editingId}`, payload);
        showToast("Servicio actualizado");
      } else {
        await apiClient.post("/services", payload);
        showToast("Servicio creado");
      }
      setShowModal(false);
      await loadServices();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo guardar el servicio"),
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = async (s: Service) => {
    setTogglingId(s.id);
    try {
      await apiClient.put(`/services/${s.id}`, { isActive: !s.isActive });
      await loadServices();
    } catch (err) {
      showToast(
        extractApiErrorMessage(
          err,
          "No se pudo cambiar el estado del servicio",
        ),
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (s: Service) => {
    if (
      !(await confirm({
        title: `¿Eliminar el servicio "${s.name}"?`,
        message: "Esta acción no se puede deshacer.",
        confirmText: "Sí, eliminar",
        danger: true,
      }))
    )
      return;
    try {
      setDeletingId(s.id);
      const res = await apiClient.delete(`/services/${s.id}`);
      showToast("Servicio eliminado");
      const warnings = res.data?.warnings as string[] | undefined;
      if (Array.isArray(warnings) && warnings.length > 0) {
        showToast(warnings.join(" "), "info");
      }
      await loadServices();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo eliminar el servicio"),
        "error",
      );
    } finally {
      setDeletingId(null);
    }
  };

  /** Mueve un servicio una posición arriba/abajo dentro de `services` (que
   * ya viene ordenado por `sortOrder` desde el backend) y persiste el nuevo
   * orden completo con `PUT /services/reorder`. Actualiza en optimista para
   * que se sienta instantáneo; si falla, recarga desde el servidor para
   * revertir. Deshabilitado mientras hay una búsqueda activa: el orden
   * visual (filtrado) podría no coincidir con el orden real completo. */
  const handleMoveService = async (
    serviceId: string,
    direction: "up" | "down",
  ) => {
    const idx = services.findIndex((s) => s.id === serviceId);
    if (idx === -1) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= services.length) return;

    const reordered = [...services];
    [reordered[idx], reordered[targetIdx]] = [
      reordered[targetIdx],
      reordered[idx],
    ];
    const serviceIds = reordered.map((s) => s.id);

    setReorderingId(serviceId);
    setServices(reordered);
    try {
      await apiClient.put("/services/reorder", { serviceIds });
      await loadServices();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo reordenar el servicio"),
        "error",
      );
      await loadServices();
    } finally {
      setReorderingId(null);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          {loadError.isBookingModeMismatch ? (
            <>
              <CalendarX className="w-10 h-10 text-gray-400" />
              <p className="text-gray-600">{loadError.message}</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
              >
                Volver al panel
              </Link>
            </>
          ) : (
            <>
              <AlertTriangle className="w-10 h-10 text-red-500" />
              <p className="text-red-600">{loadError.message}</p>
              <button
                onClick={loadServices}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Reintentar
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-emerald-600" />
            Servicios
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Catálogo de servicios que ofrece tu negocio (corte, masaje,
            consulta…)
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={!canManage}
          title={canManage ? undefined : "No tienes permiso para editar servicios"}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          Nuevo servicio
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, categoría o descripción"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          Mostrar inactivos
        </label>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-700">
              {services.length === 0
                ? "Aún no tienes servicios"
                : "No hay servicios que coincidan con la búsqueda"}
            </p>
            {services.length === 0 && (
              <p className="text-sm mt-1">
                Crea el primero para empezar a recibir citas.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((s) => {
                const orderIdx = services.findIndex((x) => x.id === s.id);
                const isFirst = orderIdx <= 0;
                const isLast =
                  orderIdx === -1 || orderIdx === services.length - 1;
                const reorderBlocked =
                  !canManage || !!search.trim() || reorderingId !== null;
                const reorderTitle = search.trim()
                  ? "Quita la búsqueda para reordenar"
                  : undefined;
                return (
                <div key={s.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center text-white font-semibold"
                      style={{ backgroundColor: s.color }}
                    >
                      {s.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 truncate">
                          {s.name}
                        </p>
                        {!s.isActive && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 uppercase">
                            Inactivo
                          </span>
                        )}
                      </div>
                      {s.category && (
                        <p className="text-xs text-gray-500">{s.category}</p>
                      )}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {s.durationMinutes} min
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Euro className="w-3 h-3" />
                          {s.price.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handleMoveService(s.id, "up")}
                          disabled={reorderBlocked || isFirst}
                          title={reorderTitle ?? "Subir"}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          {reorderingId === s.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <ArrowUp className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleMoveService(s.id, "down")}
                          disabled={reorderBlocked || isLast}
                          title={reorderTitle ?? "Bajar"}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => toggleActive(s)}
                        disabled={!canManage || togglingId === s.id}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          !canManage
                            ? "No tienes permiso para editar servicios"
                            : s.isActive
                              ? "Ocultar"
                              : "Activar"
                        }
                      >
                        {togglingId === s.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : s.isActive ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => openEdit(s)}
                        disabled={!canManage}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        disabled={!canManage}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deletingId === s.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">
                      Servicio
                    </th>
                    <th className="text-left px-4 py-3 font-medium">
                      Categoría
                    </th>
                    <th className="text-left px-4 py-3 font-medium">
                      Duración
                    </th>
                    <th className="text-left px-4 py-3 font-medium">Precio</th>
                    <th className="text-left px-4 py-3 font-medium">Estado</th>
                    <th className="text-right px-4 py-3 font-medium">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((s) => {
                    const orderIdx = services.findIndex((x) => x.id === s.id);
                    const isFirst = orderIdx <= 0;
                    const isLast =
                      orderIdx === -1 || orderIdx === services.length - 1;
                    const reorderBlocked =
                      !canManage || !!search.trim() || reorderingId !== null;
                    const reorderTitle = search.trim()
                      ? "Quita la búsqueda para reordenar"
                      : undefined;
                    return (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold"
                            style={{ backgroundColor: s.color }}
                          >
                            {s.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900">
                              {s.name}
                            </p>
                            {s.description && (
                              <p className="text-xs text-gray-500 truncate max-w-xs">
                                {s.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {s.category || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {s.durationMinutes} min
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {s.price.toFixed(2)} €
                        {s.requiresDeposit && s.depositAmount && (
                          <span className="text-xs text-gray-400 block">
                            Anticipo {s.depositAmount.toFixed(2)} €
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {s.isActive ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800">
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                            Inactivo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end items-center gap-1">
                          <button
                            onClick={() => handleMoveService(s.id, "up")}
                            disabled={reorderBlocked || isFirst}
                            title={reorderTitle ?? "Subir"}
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {reorderingId === s.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ArrowUp className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleMoveService(s.id, "down")}
                            disabled={reorderBlocked || isLast}
                            title={reorderTitle ?? "Bajar"}
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleActive(s)}
                            disabled={!canManage || togglingId === s.id}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              !canManage
                                ? "No tienes permiso para editar servicios"
                                : s.isActive
                                  ? "Ocultar"
                                  : "Activar"
                            }
                          >
                            {togglingId === s.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : s.isActive ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => openEdit(s)}
                            disabled={!canManage}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              canManage
                                ? "Editar"
                                : "No tienes permiso para editar servicios"
                            }
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(s)}
                            disabled={!canManage}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              canManage
                                ? "Eliminar"
                                : "No tienes permiso para editar servicios"
                            }
                          >
                            {deletingId === s.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-lg md:rounded-xl rounded-t-2xl shadow-xl max-h-[90dvh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? "Editar servicio" : "Nuevo servicio"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Corte de pelo, Masaje relajante…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    placeholder="Peluquería"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) =>
                        setFormData({ ...formData, color: e.target.value })
                      }
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <div className="flex flex-wrap gap-1">
                      {COLOR_PRESETS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setFormData({ ...formData, color: c })}
                          className={`w-6 h-6 rounded-full border-2 ${
                            formData.color === c
                              ? "border-gray-900"
                              : "border-white"
                          }`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duración (min) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min={5}
                    max={480}
                    value={formData.durationMinutes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        durationMinutes: e.target.value,
                      })
                    }
                    onFocus={(e) => e.target.select()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Intervalo entre slots (min)
                  </label>
                  <input
                    type="number"
                    min={5}
                    max={180}
                    step={5}
                    value={formData.slotInterval}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        slotInterval: e.target.value,
                      })
                    }
                    onFocus={(e) => e.target.select()}
                    placeholder="Genérico"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Vacío = usa el intervalo genérico de Reglas de reserva. Si
                    lo pones (entre 5 y 180 min), este servicio se ofrece cada
                    X minutos.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio (€)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    onFocus={(e) => e.target.select()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isActive: e.target.checked,
                        })
                      }
                      className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Servicio activo
                  </label>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requiresDeposit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        requiresDeposit: e.target.checked,
                      })
                    }
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Requiere anticipo / depósito
                </label>
                {formData.requiresDeposit && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Importe del anticipo (€)
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.depositAmount}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          depositAmount: e.target.value,
                        })
                      }
                      onFocus={(e) => e.target.select()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !canManage}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingId ? "Guardar cambios" : "Crear servicio"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
